import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { LockIcon, PencilIcon, XIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { orderTotals } from '../../utils/checkout';
import { goToShop } from '../../utils/shopIntent';
import { buttonClasses } from '../../utils/buttonStyles';
import { UpgradeOfferCard } from './UpgradeOfferCard';
import { SmartSavingsNotice } from './SmartSavingsNotice';
import { AddSystemPanel } from './AddSystemPanel';
import { OrderSummary } from './OrderSummary';
const m = (n: number) => formatMoney(n, 'en');

/** "Complete your MarineForceLED System" — between any purchase button and Shopify checkout. */
export function PreCheckoutSheet() {
  const {
    state,
    order,
    checkout,
    close
  } = useFunnel();
  const reduce = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);
  const open = state !== null;
  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => dialogRef.current?.focus(), 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab' && dialogRef.current) {
        const f = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prevFocus?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const total = orderTotals(order).total;
  const selected = state ? getBundle(state.primary.bundleId) : null;
  const editSystem = () => {
    const id = state?.primary.bundleId;
    close();
    if (id) goToShop(id);
  };
  return <AnimatePresence data-id="element-112" data-mp="cujwn65nf10p">
      {open && state && selected && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-[#030A14]/85 backdrop-blur-sm md:items-center md:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={close} data-id="element-113" data-mp="hls807qjgwgi">
          <motion.div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="precheckout-title" className="relative flex h-[94dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl border border-white/15 bg-navy shadow-2xl focus:outline-none md:h-auto md:max-h-[90vh] md:rounded-2xl" initial={reduce ? {
        opacity: 0
      } : {
        y: 40,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        y: 40,
        opacity: 0
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-114" data-mp="3q2wrik6hrbnm">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 pb-4 pt-5 md:px-8 md:pt-7" data-id="element-115" data-mp="3penxy72a2kyb">
              <div data-id="element-116" data-mp="72rg2okx5evd">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ice" data-id="element-117" data-mp="2zl9tgxt3now">Complete your MarineForceLED system</p>
                <h2 id="precheckout-title" className="mt-1.5 font-display text-[26px] font-semibold uppercase leading-[0.95] text-white md:text-4xl" data-id="element-118" data-mp="gzw4yw65fofv">
                  Before you go… make your system even better.
                </h2>
                <p className="mt-1.5 font-display text-sm font-semibold uppercase tracking-[0.12em] text-titanium" data-id="element-119" data-mp="w01jzrrqkkfg">
                  More lighting. More backup. Better value.
                </p>
              </div>
              <button type="button" onClick={close} aria-label="Close" className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-titanium transition-colors duration-150 hover:bg-white/5 hover:text-white" data-id="element-120" data-mp="19tj53h02m57l">
                <XIcon className="h-5 w-5" aria-hidden data-id="element-121" data-mp="m480foflnqo6" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto overscroll-contain md:grid md:grid-cols-[1fr_360px]" data-id="element-122" data-mp="r42shvb42mat">
              <div className="space-y-4 px-5 py-5 md:px-8 md:py-6" data-id="element-123" data-mp="1xi25798yvbol">
                <SelectedCard data-id="element-124" data-mp="22hy8f69uaz33" />
                <UpgradeOfferCard data-id="element-125" data-mp="22gwvew99k2iq" />
                <SmartSavingsNotice data-id="element-126" data-mp="22kaxs4hxgbq6" />
                <AddSystemPanel data-id="element-127" data-mp="3n0s7yb5c75gp" />
              </div>
              <aside className="border-t border-white/10 bg-[#06121F] px-5 py-6 md:border-l md:border-t-0 md:px-6" data-id="element-128" data-mp="3n2bps66n5okl">
                <OrderSummary data-id="element-129" data-mp="3i3idui7065mj" />
                <div className="mt-6 hidden md:block" data-id="element-130" data-mp="3d9nujfvb7snz">
                  <button type="button" onClick={checkout} className={`${buttonClasses('primary', 'lg')} w-full`} data-id="element-131" data-mp="3d8ctvox5v113">
                    <LockIcon className="h-4 w-4" aria-hidden data-id="element-132" data-mp="349rd9zb91w2y" /> Continue to secure checkout
                  </button>
                  <button type="button" onClick={editSystem} className={`${buttonClasses('ghost')} mt-2 w-full`} data-id="element-133" data-mp="2za4xbi41e1qg">
                    <PencilIcon className="h-4 w-4" aria-hidden data-id="element-134" data-mp="2pcql5up5bz3y" /> Edit my system
                  </button>
                </div>
              </aside>
            </div>

            {/* Mobile sticky footer */}
            <div className="border-t border-white/10 bg-deep px-5 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 md:hidden" data-id="element-135" data-mp="nklroes00tr4">
              <button type="button" onClick={checkout} className={`${buttonClasses('primary', 'lg')} w-full !px-4`} data-id="element-136" data-mp="2i0kb1ctvcjid">
                <LockIcon className="h-4 w-4" aria-hidden data-id="element-137" data-mp="2mtdfdzlfdptx" /> Secure checkout — {m(total)}
              </button>
              <button type="button" onClick={editSystem} className="mt-1 flex h-10 w-full items-center justify-center gap-1.5 text-sm text-titanium" data-id="element-138" data-mp="28137b0y6gfp1">
                <PencilIcon className="h-4 w-4" aria-hidden data-id="element-139" data-mp="30rk0ef59zvc6" /> Edit my system
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function SelectedCard() {
  const {
    state
  } = useFunnel();
  if (!state) return null;
  const b = getBundle(state.primary.bundleId);
  const qty = state.primary.qty;
  const p = priceBundle(b);
  const lights = b.qty * qty;
  return <div className="flex items-center gap-4 border border-white/15 bg-[#081A2E] p-3 pr-5" data-id="element-140" data-mp="1gaf8hvjz7mej">
      <img src={b.image} alt={`${b.name} — ${b.qty} M1 PRO lights`} className="h-20 w-16 shrink-0 object-cover object-[50%_30%]" data-id="element-141" data-mp="1kf52fev2qqwt" />
      <div className="min-w-0 flex-1" data-id="element-142" data-mp="1cvvtfmqhcu8a">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-titanium" data-id="element-143" data-mp="17vr7hh6sz7ao">Your selected system</p>
        <p className="font-display text-xl font-semibold uppercase leading-tight text-white" data-id="element-144" data-mp="1mqxh0e53xo8r">
          {qty > 1 ? `${qty} × ` : ''}
          {b.tagline}
        </p>
        <p className="text-sm text-titanium" data-id="element-145" data-mp="u3bbh0cbk9j9">
          {lights} M1 PRO {lights === 1 ? 'light' : 'lights'} · {b.warranty === 'lifetime' ? 'Lifetime warranty' : '1-year warranty'}
        </p>
      </div>
      <span className="font-display text-2xl font-semibold text-white" data-id="element-146" data-mp="13twa2nrzte26">{m(Math.round(p.total * qty * 100) / 100)}</span>
    </div>;
}